import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getCsrfToken } from '../utils/auth';
import '../styles/Invite.css';

/**
 * "Invite" in the header, and the little form behind it.
 *
 * The button renders ONLY when the server says this deployment can send mail
 * (`GET /api/invite/config` → `enabled`). A deployment without SMTP shows nothing at
 * all rather than a button that answers 503 — the same bargain the Ask Paul button
 * makes with its two environment variables.
 *
 * What the invitation actually is, is worth keeping in mind while reading this: an
 * email with a link to the login screen. It carries no token and grants nothing
 * (Critical Note #73); the recipient signs in with their own Orcanos credentials, and
 * that is what the email says in so many words.
 */

const MAX_NOTE = 500;

async function api(method, url, body) {
  const opts = { method, headers: {}, credentials: 'include' };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  if (method !== 'GET') opts.headers['X-CSRF-Token'] = getCsrfToken();
  const res = await fetch(url, opts);
  let data = null;
  try { data = await res.json(); } catch (_) { /* a 500 can arrive as HTML */ }
  if (!res.ok) throw new Error(data?.detail || `Request failed (${res.status})`);
  return data;
}

function InviteButton({ enabled = true }) {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [emails, setEmails] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    api('GET', '/api/invite/config')
      .then((d) => { if (alive) setConfig(d); })
      .catch(() => { if (alive) setConfig({ enabled: false }); });
    return () => { alive = false; };
  }, [enabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    if (firstFieldRef.current) firstFieldRef.current.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    setError('');
    setResult(null);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    // One field, split on commas, semicolons, spaces and newlines — because a list of
    // addresses is something people PASTE, and it arrives in whichever of those forms
    // the program they copied it from happened to use.
    const list = emails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) {
      setError('Enter at least one email address.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const data = await api('POST', '/api/invite', { emails: list, note: note.trim() });
      setResult(data);
      setEmails('');
      setNote('');
      setConfig((c) => ({ ...(c || {}), remaining_today: data.remaining_today }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (!enabled || !config?.enabled) return null;

  return (
    <>
      <button
        type="button"
        className="invite-trigger"
        onClick={() => { setResult(null); setError(''); setOpen(true); }}
        title="Invite a colleague to sign in"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM2 16a6 6 0 1112 0v1H2v-1zM16 6a1 1 0 011 1v2h2a1 1 0 110 2h-2v2a1 1 0 11-2 0v-2h-2a1 1 0 110-2h2V7a1 1 0 011-1z" />
        </svg>
        <span className="invite-trigger-label">Invite</span>
      </button>

      {open && (
        <div className="invite-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
          <div className="invite-modal" role="dialog" aria-modal="true" aria-labelledby="invite-title">
            <div className="invite-head">
              <div>
                <h2 id="invite-title">Invite people to the Traceability Matrix</h2>
                <p className="invite-sub">
                  They&rsquo;ll get an email with a link, and they sign in with their own
                  Orcanos credentials — this invitation grants no access by itself.
                </p>
              </div>
              {/* Always reachable, whatever state the form is in (the export modal's rule). */}
              <button type="button" className="invite-x" onClick={close} aria-label="Close">×</button>
            </div>

            {result ? (
              <div className="invite-body">
                <div className="invite-done">
                  <div className="invite-done-mark" aria-hidden="true">✓</div>
                  <div>
                    <strong>
                      {result.sent.length === 1
                        ? 'Invitation sent.'
                        : `${result.sent.length} invitations sent.`}
                    </strong>
                    <div className="invite-done-list">{result.sent.join(', ')}</div>
                    {result.failed?.length > 0 && (
                      <div className="invite-failed">
                        Could not send to: {result.failed.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
                <div className="invite-actions">
                  <button type="button" className="invite-secondary" onClick={() => setResult(null)}>
                    Invite someone else
                  </button>
                  <button type="button" className="invite-primary" onClick={close}>Done</button>
                </div>
              </div>
            ) : (
              <form className="invite-body" onSubmit={submit}>
                <label className="invite-label" htmlFor="invite-emails">Email addresses</label>
                <textarea
                  id="invite-emails"
                  ref={firstFieldRef}
                  className="invite-input invite-emails"
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  placeholder="dana@company.com, sam@company.com"
                  rows={3}
                  disabled={sending}
                />
                <div className="invite-hint">
                  Separate addresses with a comma, a space or a new line
                  {config.max_recipients ? ` — up to ${config.max_recipients} at a time` : ''}.
                  Each person gets their own copy; nobody sees the other addresses.
                </div>

                <label className="invite-label" htmlFor="invite-note">
                  A note (optional)
                </label>
                <textarea
                  id="invite-note"
                  className="invite-input"
                  value={note}
                  maxLength={MAX_NOTE}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="We're tracking the DHF here now — come and have a look."
                  rows={3}
                  disabled={sending}
                />

                {error && <div className="invite-error">{error}</div>}

                <div className="invite-actions">
                  {typeof config.remaining_today === 'number' && config.remaining_today < 15 && (
                    <span className="invite-remaining">
                      {config.remaining_today} invitations left today
                    </span>
                  )}
                  <button type="button" className="invite-secondary" onClick={close} disabled={sending}>
                    Cancel
                  </button>
                  <button type="submit" className="invite-primary" disabled={sending}>
                    {sending ? 'Sending…' : 'Send invitation'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default InviteButton;
